// @flow
import React from 'react'
import styled from 'styled-components'
import { Input, Checkbox } from 'antd'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'
import { mediaMax, mediaMin } from 'frontend_core/dist/styles/media'
import Colors from 'components/styles/colors'
import Button from 'components/atoms/Button'
import Title from 'components/atoms/Title'
import LinkText from 'components/atoms/LinkText'
import Icon from 'components/atoms/Icon'
import Description from 'components/atoms/Description'
import FloatBottom from 'components/molecules/FloatBottom'
import ReputationStar from 'components/molecules/ReputationStar'
import InputContainer from 'components/molecules/InputContainer'
import FormItem from 'components/molecules/FormItem'
import CheckboxList from 'components/molecules/CheckboxList'
import RadioListRecommended from 'components/molecules/RadioListRecommended'
import FormSectionTitle from 'components/molecules/FormSectionTitle'

const Section = styled.div`
  margin-top: ${spacing.large}px;
`
const H2 = styled.h2`
  font-size: ${fontSize.medium}px;
  font-weight: bold;
  ${mediaMin.phone`
    font-size: ${fontSize.medium_2}px;
  `};
`
const SubSection = styled.div`
  margin-top: ${spacing.small_3}px;
  border-top: 1px solid ${Colors.grayE};
`
const LinkTextWrapper = styled.div`
  margin-top: ${spacing.small_3}px;
`
const SectionSubTitleWrapper = styled.div`
  margin-top: ${spacing.medium_2}px;
`
const SectionSubTitle = styled.p`
  font-size: ${fontSize.small_3}px;
  font-weight: bold;
`
const ReputationStarWrapper = styled.div`
  margin-top: ${spacing.small_2}px;
  ${mediaMin.phone`
    margin-top: ${spacing.small_3}px;
  `};
`
const FormWrapper = styled.div`
  margin-top: ${spacing.medium}px;
`
const CheckboxListWrapper = styled.div`
  margin-top: ${spacing.medium}px;
  ${mediaMin.phone`
    margin-top: ${spacing.medium_2}px;
  `};
`
const ButtonWrapper = styled.span`
  display: inline-block;
  width: 50%;

  &:first-of-type {
    padding-right: ${spacing.small}px;
  }

  &:nth-of-type(2n) {
    padding-left: ${spacing.small}px;
  }

  ${mediaMin.phone`
    width: auto;
  `};
`
const AttentionWrapper = styled.div`
  margin-top: ${spacing.large}px;
`
const AttentionTitle = styled.p`
  font-size: ${fontSize.medium}px;
`
const AttentionBlock = styled.div`
  margin-top: ${spacing.small_3}px;
  ${mediaMin.phone`
    margin-top: ${spacing.medium}px;
  `};
`
const SectionDesc = styled.p`
  margin-top: ${spacing.small_2}px;
  font-size: ${fontSize.small_2}px;
  color: ${Colors.gray9};
`
const RadioListRecommendedWrapper = styled.div`
  margin-top: ${spacing.medium}px;
  ${mediaMin.phone`
    margin-top: ${spacing.medium_3}px;
  `};
`
const FormSectionWrapper = styled.div`
  margin-top: ${spacing.medium_3}px;

  :first-of-type {
    margin-top: ${spacing.medium_2}px;
  }

  ${mediaMin.phone`
    margin-top: ${spacing.large}px;
    :first-of-type {
      margin-top: ${spacing.large}px;
    }
  `};
`

export type PropTypes = {
  score: {
    value?: number,
    onChange: Function,
  },
  description: {
    value?: string,
    errorTexts?: Array<string>,
    onChange: Function,
  },
  roomFeatures: {
    value?: Array<{ label: string, value: string, checked: boolean }>,
    onChange: Function,
  },
  roomNps: {
    value?: number,
    onChange: Function,
  },
  roomDescription: {
    value?: string,
    onChange: Function,
  },
  nps: {
    value?: number,
    onChange: Function,
  },
  serviceDescription: {
    value?: string,
    onChange: Function,
  },
  submitButton: {
    onClick: Function,
    isDisabled?: ?boolean,
  },
  onClickBackToMessage: Function,
}

export default function ReservationReputationForm(props: PropTypes) {
  return (
    <div>
      <Title>{i18n.t('page.dashboard.reservation.postReview')}</Title>
      <LinkTextWrapper>
        <LinkText onClick={props.onClickBackToMessage}>
          <Icon
            type="angle-left"
            positionType="left"
            fontSizePc={fontSize.small_2}
          />
          {i18n.t('page.dashboard.reservation.backToMessage')}
        </LinkText>
      </LinkTextWrapper>
      <Section>
        <H2>{i18n.t('page.dashboard.reservation.publicReview')}</H2>
        <SubSection>
          <FormSectionWrapper>
            <FormSectionTitle
              title={i18n.t('page.dashboard.reservation.reviewSpace')}
              description={i18n.t(
                'page.dashboard.reservation.reviewFooterInfo',
              )}
              required
            />
            <SectionSubTitleWrapper>
              <SectionSubTitle>
                {i18n.t('page.dashboard.reservation.clickStarToReview')}
              </SectionSubTitle>
            </SectionSubTitleWrapper>
            <ReputationStarWrapper>
              <ReputationStar
                touchable
                fontSizePc={24}
                fontSizeSp={28}
                score={props.score.value}
                onChange={props.score.onChange}
              />
            </ReputationStarWrapper>
            <FormWrapper>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input.TextArea
                      value={props.description.value}
                      rows={4}
                      placeholder={i18n.t(
                        'page.dashboard.reservation.reviewPlaceholder',
                      )}
                      onChange={props.description.onChange}
                    />
                  </InputContainer>
                )}
                errorTexts={props.description.errorTexts}
              />
            </FormWrapper>
          </FormSectionWrapper>
          <FormSectionWrapper>
            <FormSectionTitle
              title={i18n.t('page.dashboard.reservation.featureOfSpaceTitle')}
              description={i18n.t('form.multipleChoiceAllow')}
              required
            />
            <CheckboxListWrapper>
              <CheckboxList
                checkboxList={props.roomFeatures.value}
                onChange={props.roomFeatures.onChange}
              />
            </CheckboxListWrapper>
            <AttentionWrapper>
              <AttentionTitle>
                {i18n.t('page.dashboard.reservation.attention')}
              </AttentionTitle>
              <AttentionBlock>
                <Description>
                  {i18n.t('page.dashboard.reservation.reviewFooterEdit')}
                </Description>
                <Description>
                  {i18n.t('page.dashboard.reservation.reviewFooterTerm')}
                </Description>
                <Description>
                  {i18n.t('page.dashboard.reservation.reviewFooterInfo')}
                </Description>
              </AttentionBlock>
            </AttentionWrapper>
          </FormSectionWrapper>
        </SubSection>
      </Section>
      <Section>
        <H2>{i18n.t('page.dashboard.reservation.privateReview')}</H2>
        <SectionDesc>
          {i18n.t('page.dashboard.reservation.privateReviewDesc')}
        </SectionDesc>
        <SubSection>
          <FormSectionWrapper>
            <FormSectionTitle
              title={i18n.t('page.dashboard.reservation.recommendedSpace')}
              required
            />
            <RadioListRecommendedWrapper>
              <RadioListRecommended
                selectedValue={props.roomNps.value}
                onChange={props.roomNps.onChange}
              />
            </RadioListRecommendedWrapper>
          </FormSectionWrapper>
          <FormSectionWrapper>
            <FormSectionTitle
              title={i18n.t('page.dashboard.reservation.opinionForSpace')}
              description={i18n.t(
                'page.dashboard.reservation.opinionForSpaceFooter',
              )}
              required={false}
            />
            <FormWrapper>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input.TextArea
                      value={props.roomDescription.value}
                      rows={4}
                      placeholder={i18n.t(
                        'page.dashboard.reservation.opinionForSpace',
                      )}
                      onChange={props.roomDescription.onChange}
                    />
                  </InputContainer>
                )}
              />
            </FormWrapper>
          </FormSectionWrapper>
          <FormSectionWrapper>
            <FormSectionTitle
              title={i18n.t('page.dashboard.reservation.recommendedService')}
              required
            />
            <RadioListRecommendedWrapper>
              <RadioListRecommended
                selectedValue={props.nps.value}
                onChange={props.nps.onChange}
              />
            </RadioListRecommendedWrapper>
          </FormSectionWrapper>
          <FormSectionWrapper>
            <FormSectionTitle
              title={i18n.t('page.dashboard.reservation.opinionForService')}
              description={i18n.t(
                'page.dashboard.reservation.opinionForServiceFooter',
              )}
              required={false}
            />
            <FormWrapper>
              <FormItem
                renderInput={() => (
                  <InputContainer>
                    <Input.TextArea
                      value={props.serviceDescription.value}
                      rows={4}
                      placeholder={i18n.t(
                        'page.dashboard.reservation.opinionForService',
                      )}
                      onChange={props.serviceDescription.onChange}
                    />
                  </InputContainer>
                )}
              />
            </FormWrapper>
          </FormSectionWrapper>
        </SubSection>
      </Section>
      <FloatBottom inbox textAlign="right" hasSidebar>
        <ButtonWrapper>
          <Button
            onClick={props.onClickBackToMessage}
            tag="button"
            colorType="gray"
            buttonType="medium"
            fullWidthSp
            buttonWidthPc="160px"
          >
            {i18n.t('page.shared.cancel')}
          </Button>
        </ButtonWrapper>
        <ButtonWrapper>
          <Button
            onClick={props.submitButton.onClick}
            tag="button"
            colorType="blue"
            buttonType="medium"
            fullWidthSp
            buttonWidthPc="160px"
            disabled={props.submitButton.isDisabled}
          >
            {i18n.t('page.shared.post')}
          </Button>
        </ButtonWrapper>
      </FloatBottom>
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/organisms/ReservationReputationForm/index.jsx